import { defineComponent as _defineComponent } from 'vue'
import store from "@/store";
  import router from "@/router";
  import { computed, onMounted, onUnmounted, ref, watch } from "vue";
  import {
    normalizeEoiDetails,
    normalizeEstateContacts,
    normalizeLotDetails,
  } from "@/helpers/customer/normalizer";
  import {
    EoiDetails,
    Lot,
    LotStatusObject,
    LotTab,
    ResponseEoiDetails,
  } from "@/types/CustomerLotDetails";
  import SwitchComponent from "@/components/ui/SwitchComponent.vue";
  import {
    dynamicStatuses,
    eoiStatusesHavingBackground,
    getTabByUrl,
    getValueByStatus,
    isEoiApprovingStatus,
    isEoiPendingStatus,
    lotTabs,
  } from "@/helpers/customer";
  import { EoiStatus } from "@/enums/EoiStatus";
  import { getUser } from "@/features/useUser";
  import { LotMapInterface } from "@/types/LotMapInterface";
  import { LotStatus } from "@/enums/LotStatus";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import { getFirstLetter } from "@/helpers/transformation/string";
  import Popper from "vue3-popper";

  const mobileLayoutWidth = 769;
  
export default /*@__PURE__*/_defineComponent({
  __name: 'CustomerLotDetailsView',
  setup(__props, { expose: __expose }) {
  __expose();

  const { isDesktop } = useIsDesktop(mobileLayoutWidth);

  const { eoiId, id: lotId } = router.currentRoute.value.params;
  const lot = ref({} as Lot);
  const eoiDetails = ref<EoiDetails>([]);
  const actions = ref([]);
  const estateContacts = ref({});
  const lotMap = ref<LotMapInterface | null>(null);
  const isFetchingLot = ref(false);
  const activeTab = ref({} as LotTab);

  const currentStatusIndex = computed(() => {
    if (isEoiPendingStatus(eoiDetails.value.status)) {
      return 0;
    }

    if (
      isEoiApprovingStatus(eoiDetails.value.status) &&
      eoiDetails.value.status === EoiStatus.DECLINED
    ) {
      return 1;
    }

    if (eoiDetails.value.status === EoiStatus.WITHDRAWN) {
      return 3;
    }

    if (
      [EoiStatus.REFUND_DECLINED, EoiStatus.REFUNDED].includes(
        eoiDetails.value.status
      )
    ) {
      return 4;
    }

    if (lot.value.status === LotStatus.EOI) {
      return 1;
    }

    return dynamicStatuses(lot.value.status, eoiDetails.value.status).findIndex(
      (status) => status.value === lot.value.status
    );
  });

  let timerId: number;

  watch(
    () => eoiDetails.value?.status,
    (newValue, oldValue) => {
      if (newValue === EoiStatus.WAITING_FOR_DECLINED) {
        timerId = setInterval(() => {
          fetchEoiDetails();
        }, 2000);
      }

      if (
        newValue !== EoiStatus.WAITING_FOR_DECLINED &&
        oldValue === EoiStatus.WAITING_FOR_DECLINED
      ) {
        clearInterval(timerId);
      }
    }
  );

  function isShowLifecycleLabel(index: number) {
    return (
      currentStatusIndex.value <= index ||
      (eoiDetails.value.status !== EoiStatus.APPROVED &&
        getActiveStatus()?.value === LotStatus.EOI)
    );
  }
  function onEstateTitle() {
    router.push({
      name: "EstateMap",
      params: { estateId: lot.value.estate.id },
    });
  }

  function onBack() {
    router.push({ name: "CustomerLots" });
  }
  function onChangeTab(tab: LotTab) {
    router.push(`/customer/lots/${lotId}/eoi/${eoiId}/${tab.value}`);
  }
  function fetchEoiDetails() {
    store
      .dispatch("eoi/getEois", {
        customerId: getUser()._id,
        lotId: lotId,
      })
      .then((response) => {
        const currentEoi = response.data.find(
          (item: ResponseEoiDetails) => item._id === eoiId
        );

        if (currentEoi) {
          eoiDetails.value = normalizeEoiDetails(currentEoi);
        }
      });
  }
  function fetchLotActions() {
    store.dispatch("lot/getLotActions", lotId).then((response) => {
      actions.value = response.data;
    });
  }
  function fetchLot() {
    isFetchingLot.value = true;

    setTimeout(() => {
      return store
        .dispatch("lot/getLot", lotId)
        .then((response) => {
          lot.value = normalizeLotDetails(response.data);
          fetchEstate(response.data.estateId._id);
        })
        .finally(() => {
          isFetchingLot.value = false;
        });
    }, 600);
  }
  function fetchEstate(id: string) {
    store.dispatch("estate/getEstate", id).then((response) => {
      estateContacts.value = normalizeEstateContacts(response.data);
    });
  }
  function getAdditionalStatusClass(index: number, status: LotStatusObject) {
    if (
      eoiStatusesHavingBackground.includes(
        eoiDetails.value.status as EoiStatus
      ) &&
      currentStatusIndex.value >= index
    ) {
      return [getValueByStatus(status.value, "background")];
    }

    return "";
  }
  function getActiveStatus() {
    return dynamicStatuses(lot.value.status, eoiDetails.value.status)[
      currentStatusIndex.value
    ];
  }
  function setActiveTab(): void {
    const { fullPath } = router.currentRoute.value;

    activeTab.value = getTabByUrl(fullPath) || ({} as LotTab);
  }

  function getLotMap() {
    store.dispatch("lot/getLotMap", lotId).then((res) => {
      lotMap.value = res.data;
    });
  }
  function getLifecycleItemLabel(index: number, status: string) {
    if (
      !isDesktop.value &&
      eoiDetails.value.status === EoiStatus.REFUND_DECLINED
    ) {
      return getFirstLetter(status);
    }

    if (
      isDesktop.value ||
      (eoiStatusesHavingBackground.includes(
        eoiDetails.value.status as EoiStatus
      ) &&
        currentStatusIndex.value >= index)
    ) {
      return status;
    } else {
      return getFirstLetter(status);
    }
  }
  function changeTab(value) {
    const tab = lotTabs.find((tab) => tab.value === value);
    const index = lotTabs.findIndex((tab) => tab.value === value);

    onChangeTab(tab);
    activeTab.value = lotTabs[index];
  }
  function onWithdraw() {
    fetchEoiDetails();
  }
  function completeEoi() {
    router.push({
      name: "EOIRequest",
      params: {
        estateId: lot.value.estate.id,
        lotId: lot.value.id,
        backUrl: router.currentRoute.value.path,
      },
      query: {
        eoiId: eoiDetails.value.id,
      },
    });
  }
  const onPayAgain = () => {
    const params = {
      eoiId: eoiDetails.value.id,
      estateId: lot.value.estate.id,
      landownerId: lot.value.landowner.id,
      lotId: lot.value.id,
    };

    store.dispatch("eoi/getEoiPaymentUrl", params).then((response) => {
      window.open(response.data);
    });
  };

  onMounted(() => {
    setActiveTab();
    fetchLot();
    getLotMap();
    fetchEoiDetails();
    fetchLotActions();
  });
  onUnmounted(() => clearInterval(timerId));

const __returned__ = { mobileLayoutWidth, isDesktop, eoiId, lotId, lot, eoiDetails, actions, estateContacts, lotMap, isFetchingLot, activeTab, currentStatusIndex, get timerId() { return timerId }, set timerId(v) { timerId = v }, isShowLifecycleLabel, onEstateTitle, onBack, onChangeTab, fetchEoiDetails, fetchLotActions, fetchLot, fetchEstate, getAdditionalStatusClass, getActiveStatus, setActiveTab, getLotMap, getLifecycleItemLabel, changeTab, onWithdraw, completeEoi, onPayAgain, get store() { return store }, get router() { return router }, computed, onMounted, onUnmounted, ref, watch, get normalizeEoiDetails() { return normalizeEoiDetails }, get normalizeEstateContacts() { return normalizeEstateContacts }, get normalizeLotDetails() { return normalizeLotDetails }, get EoiDetails() { return EoiDetails }, get Lot() { return Lot }, get LotStatusObject() { return LotStatusObject }, get LotTab() { return LotTab }, get ResponseEoiDetails() { return ResponseEoiDetails }, SwitchComponent, get dynamicStatuses() { return dynamicStatuses }, get eoiStatusesHavingBackground() { return eoiStatusesHavingBackground }, get getTabByUrl() { return getTabByUrl }, get getValueByStatus() { return getValueByStatus }, get isEoiApprovingStatus() { return isEoiApprovingStatus }, get isEoiPendingStatus() { return isEoiPendingStatus }, get lotTabs() { return lotTabs }, get EoiStatus() { return EoiStatus }, get getUser() { return getUser }, get LotMapInterface() { return LotMapInterface }, get LotStatus() { return LotStatus }, get useIsDesktop() { return useIsDesktop }, get getFirstLetter() { return getFirstLetter }, get Popper() { return Popper } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})